import React, { useState, useEffect, Suspense } from 'react';
import loadable from '@loadable/component';
import { useAB } from '@guardian/ab-react';
import { tests } from '@frontend/web/experiments/ab-tests';
import { ShareCount } from '@frontend/web/components/ShareCount';
import { MostViewedFooter } from '@frontend/web/components/MostViewed/MostViewedFooter/MostViewedFooter';
import { CalloutBlockComponent } from '@root/src/web/components/elements/CalloutBlockComponent';
import { ReaderRevenueLinks } from '@frontend/web/components/ReaderRevenueLinks';
import { SlotBodyEnd } from '@root/src/web/components/SlotBodyEnd/SlotBodyEnd';
import { Links } from '@frontend/web/components/Links';
import { SubNav } from '@frontend/web/components/SubNav/SubNav';
import { GetMatchNav } from '@frontend/web/components/GetMatchNav';
import { Discussion } from '@frontend/web/components/Discussion';
import { StickyBottomBanner } from '@root/src/web/components/StickyBottomBanner/StickyBottomBanner';
import { SignInGateSelector } from '@root/src/web/components/SignInGate/SignInGateSelector';
import { incrementWeeklyArticleCount } from '@guardian/automat-client';
import { QandaAtom, GuideAtom, ProfileAtom, TimelineAtom, ChartAtom, PersonalityQuizAtom, KnowledgeQuizAtom, } from '@guardian/atoms-rendering';
import { AudioAtomWrapper } from '@frontend/web/components/AudioAtomWrapper';
import { Portal } from '@frontend/web/components/Portal';
import { HydrateOnce } from '@frontend/web/components/HydrateOnce';
import { Lazy } from '@frontend/web/components/Lazy';
import { Placeholder } from '@root/src/web/components/Placeholder';
import { decideTheme } from '@root/src/web/lib/decideTheme';
import { decideDisplay } from '@root/src/web/lib/decideDisplay';
import { decideDesign } from '@root/src/web/lib/decideDesign';
import { loadScript } from '@root/src/web/lib/loadScript';
import { useOnce } from '@root/src/web/lib/useOnce';
import { initPerf } from '@root/src/web/browser/initPerf';
import { getCookie, addCookie } from '@root/src/web/browser/cookie';
import { getCountryCode } from '@frontend/web/lib/getCountryCode';
import { getUser } from '@root/src/web/lib/getUser';
import { FocusStyleManager } from '@guardian/src-foundations/utils';
import { incrementAlreadyVisited } from '@root/src/web/lib/alreadyVisited';
import { incrementDailyArticleCount } from '@frontend/web/lib/dailyArticleCount';
import { getArticleCountConsent } from '@frontend/web/lib/contributions';
import { buildAdTargeting } from '@root/src/lib/ad-targeting';
import { cmp, onConsentChange, getConsentFor, } from '@guardian/consent-management-platform';
import { injectPrivacySettingsLink } from '@root/src/web/lib/injectPrivacySettingsLink';
import { updateIframeHeight } from '@root/src/web/browser/updateIframeHeight';
import { ClickToView } from '@root/src/web/components/ClickToView';
import { LabsHeader } from '@root/src/web/components/LabsHeader';
import { DocumentBlockComponent } from '@root/src/web/components/elements/DocumentBlockComponent';
import { EmbedBlockComponent } from '@root/src/web/components/elements/EmbedBlockComponent';
import { UnsafeEmbedBlockComponent } from '@root/src/web/components/elements/UnsafeEmbedBlockComponent';
import { InstagramBlockComponent } from '@root/src/web/components/elements/InstagramBlockComponent';
import { MapEmbedBlockComponent } from '@root/src/web/components/elements/MapEmbedBlockComponent';
import { SpotifyBlockComponent } from '@root/src/web/components/elements/SpotifyBlockComponent';
import { VideoFacebookBlockComponent } from '@root/src/web/components/elements/VideoFacebookBlockComponent';
import { VineBlockComponent } from '@root/src/web/components/elements/VineBlockComponent';
import { remoteRrHeaderLinksTestName } from '@root/src/web/experiments/tests/remoteRrHeaderLinksTest';
import { submitComponentEvent, } from '../browser/ophan/ophan';
import { trackPerformance } from '../browser/ga/ga';
import { decidePalette } from '../lib/decidePalette';
import { buildBrazeMessages } from '../lib/braze/buildBrazeMessages';
// *******************************
// ****** Dynamic imports ********
// *******************************
const EditionDropdown = loadable(() => import('@frontend/web/components/EditionDropdown'), {
    resolveComponent: (module) => module.EditionDropdown,
});
const MostViewedRightWrapper = React.lazy(() => {
    const { start, end } = initPerf('MostViewedRightWrapper');
    start();
    return import(
    /* webpackChunkName: "MostViewedRightWrapper" */ '@frontend/web/components/MostViewed/MostViewedRight/MostViewedRightWrapper').then((module) => {
        end();
        return { default: module.MostViewedRightWrapper };
    });
});
const OnwardsUpper = React.lazy(() => {
    const { start, end } = initPerf('OnwardsUpper');
    start();
    return import(
    /* webpackChunkName: "OnwardsUpper" */ '@frontend/web/components/Onwards/OnwardsUpper').then((module) => {
        end();
        return { default: module.OnwardsUpper };
    });
});
const OnwardsLower = React.lazy(() => {
    const { start, end } = initPerf('OnwardsLower');
    start();
    return import(
    /* webpackChunkName: "OnwardsLower" */ '@frontend/web/components/Onwards/OnwardsLower').then((module) => {
        end();
        return { default: module.OnwardsLower };
    });
});
const GetMatchStats = React.lazy(() => {
    const { start, end } = initPerf('GetMatchStats');
    start();
    return import(
    /* webpackChunkName: "GetMatchStats" */ '@frontend/web/components/GetMatchStats').then((module) => {
        end();
        return { default: module.GetMatchStats };
    });
});
export const App = ({ CAPI, NAV, ophanRecord }) => {
    const [isSignedIn, setIsSignedIn] = useState();
    const [user, setUser] = useState();
    const [countryCode, setCountryCode] = useState();
    // This is an async version of the countryCode state value defined above.
    // This can be used where you've got logic which depends on countryCode but
    // don't want to block on it becoming available, as you would with the
    // non-async version (this is the case in the banner picker where some
    // banners need countryCode but we don't want to block all banners from
    // executing their canShow logic until countryCode is available):
    const [asyncCountryCode, setAsyncCountryCode] = useState();
    const [brazeMessages, setBrazeMessages] = useState();
    const pageViewId = window.guardian?.config?.ophan?.pageViewId;
    const componentEventHandler = (componentType, id, action) => () => {
        const componentEvent = {
            component: {
                componentType,
                id,
                products: [],
                labels: [],
            },
            action,
        };
        submitComponentEvent(componentEvent, ophanRecord);
    };
    // *******************************
    // ** Setup AB Test Tracking *****
    // *******************************
    const ABTestAPI = useAB();
    useEffect(() => {
        const allRunnableTests = ABTestAPI.allRunnableTests(tests);
        ABTestAPI.trackABTests(allRunnableTests);
        ABTestAPI.registerImpressionEvents(allRunnableTests);
        ABTestAPI.registerCompleteEvents(allRunnableTests);
    }, [ABTestAPI]);
    useEffect(() => {
        setIsSignedIn(!!getCookie('GU_U'));
    }, []);
    useOnce(() => {
        // useOnce means this code will only run once isSignedIn is defined, and only
        // run one time
        if (isSignedIn) {
            getUser(CAPI.config.discussionApiUrl)
                .then((theUser) => {
                if (theUser)
                    setUser(theUser);
            })
                .catch((e) => console.error(`getUser - error: ${e}`));
        }
        else {
            setUser(null);
        }
    }, [isSignedIn, CAPI.config.discussionApiUrl]);
    useEffect(() => {
        const callFetch = () => {
            const countryCodePromise = getCountryCode();
            setAsyncCountryCode(countryCodePromise);
            countryCodePromise
                .then((cc) => setCountryCode(cc || ''))
                .catch((e) => console.error(`countryCodePromise - error: ${e}`));
        };
        callFetch();
    }, []);
    useEffect(() => {
        incrementAlreadyVisited();
    }, []);
    // Log an article view using the Slot Machine client lib
    // This function must be called once per article serving.
    // We should monitor this function call to ensure it only happens within an
    // article pages when other pages are supported by DCR.
    useEffect(() => {
        const incrementArticleCountsIfConsented = async () => {
            if (await getArticleCountConsent()) {
                incrementDailyArticleCount();
                incrementWeeklyArticleCount();
            }
        };
        incrementArticleCountsIfConsented().catch((e) => console.error(`incrementArticleCountsIfConsented - error: ${e}`));
    }, []);
    // AnniversaryAtom
    // Add a cookie for the serverside A/B test that is checked to see if we should
    // show the anniversary atom. This means that this user will not see the atom
    // on the next and following page views.
    useEffect(() => {
        if (CAPI.config.switches.anniversaryArticleHeader) {
            addCookie('X-GU-Experiment-0perc-D', 'true', 2); // 2 days to live means that the atom will show when the switch is on Wednesday AND Saturday
        }
    });
    // Ensure the focus state of any buttons/inputs in any of the Source
    // components are only applied when navigating via keyboard.
    // READ: https://www.theguardian.design/2a1e5182b/p/6691bb-accessibility/t/32e9fb
    useEffect(() => {
        FocusStyleManager.onlyShowFocusOnTabs();
    }, []);
    useEffect(() => {
        // Used internally only, so only import each function on demand
        const loadAndRun = (key) => (asExistingSupporter) => import(
        /* webpackChunkName: "readerRevenueDevUtils" */ '@frontend/web/lib/readerRevenueDevUtils')
            .then((utils) => utils[key](asExistingSupporter, CAPI.shouldHideReaderRevenue))
            /* eslint-disable no-console */
            .catch((error) => console.log('Error loading readerRevenueDevUtils', error));
        /* eslint-enable no-console */
        if (window && window.guardian) {
            window.guardian.readerRevenue = {
                changeGeolocation: loadAndRun('changeGeolocation'),
                showMeTheEpic: loadAndRun('showMeTheEpic'),
                showMeTheBanner: loadAndRun('showMeTheBanner'),
                showNextVariant: loadAndRun('showNextVariant'),
                showPreviousVariant: loadAndRun('showPreviousVariant'),
            };
        }
    }, [CAPI.shouldHideReaderRevenue]);
    // kick off the CMP...
    useEffect(() => {
        // the UI is injected automatically into the page,
        // and is not a react component, so it's
        // handled in here.
        if (CAPI.config.switches.consentManagement && countryCode) {
            const pubData = {
                platform: 'next-gen',
                browserId: getCookie('bwid') || undefined,
                pageViewId,
            };
            injectPrivacySettingsLink(); // manually updates the footer DOM because it's not hydrated
            // keep this in sync with CONSENT_TIMING in static/src/javascripts/boot.js in frontend
            // mark: CONSENT_TIMING
            let recordedConsentTime = false;
            onConsentChange(() => {
                if (!recordedConsentTime) {
                    recordedConsentTime = true;
                    cmp.willShowPrivacyMessage()
                        .then((willShow) => {
                        trackPerformance('consent', 'acquired', willShow ? 'new' : 'existing');
                    })
                        .catch((e) => console.error(`CMP willShowPrivacyMessage - error: ${e}`));
                }
            });
            cmp.init({
                country: countryCode,
                pubData,
            });
        }
    }, [countryCode, CAPI.config.switches.consentManagement, pageViewId]);
    // ************************
    // *   Google Analytics   *
    // ************************
    useEffect(() => {
        onConsentChange((state) => {
            const consentGiven = getConsentFor('google-analytics', state);
            if (consentGiven) {
                Promise.all([
                    loadScript('https://www.google-analytics.com/analytics.js'),
                    loadScript(window.guardian.gaPath),
                ]).catch((e) => console.error(`GA - error: ${e}`));
            }
            else {
                // We should never be able to directly set things to the global window object.
                // But in this case we want to stub things for testing, so it's ok to ignore this rule
                // @ts-ignore
                window.ga = null;
            }
        });
    }, []);
    const display = decideDisplay(CAPI.format);
    const design = decideDesign(CAPI.format);
    const pillar = decideTheme(CAPI.format);
    useOnce(() => {
        setBrazeMessages(buildBrazeMessages(isSignedIn, CAPI.config.idApiUrl));
    }, [isSignedIn, CAPI.config.idApiUrl]);
    const format = {
        display,
        design,
        theme: pillar,
    };
    const palette = decidePalette(format);
    const adTargeting = buildAdTargeting(CAPI.config);
    const inRemoteModuleTest = ABTestAPI.isUserInVariant(remoteRrHeaderLinksTestName, 'remote');
    // There are docs on loadable in ./docs/loadable-components.md
    const YoutubeBlockComponent = loadable(() => {
        if (CAPI.elementsToHydrate.filter((element) => element._type ===
            'model.dotcomrendering.pageElements.YoutubeBlockElement').length > 0) {
            return import('@frontend/web/components/elements/YoutubeBlockComponent');
        }
        return Promise.reject();
    }, {
        resolveComponent: (module) => module.YoutubeBlockComponent,
    });
    const RichLinkComponent = loadable(() => {
        if (CAPI.elementsToHydrate.filter((element) => element._type ===
            'model.dotcomrendering.pageElements.RichLinkBlockElement').length > 0) {
            return import('@frontend/web/components/elements/RichLinkComponent');
        }
        return Promise.reject();
    }, {
        resolveComponent: (module) => module.RichLinkComponent,
    });
    const InteractiveBlockComponent = loadable(() => {
        if (CAPI.elementsToHydrate.filter((element) => element._type ===
            'model.dotcomrendering.pageElements.InteractiveBlockElement').length > 0) {
            return import('@frontend/web/components/elements/InteractiveBlockComponent');
        }
        return Promise.reject();
    }, {
        resolveComponent: (module) => module.InteractiveBlockComponent,
    });
    // We use this function to filter the elementsToHydrate array by a particular
    // type so that we can hydrate them. We use T to force the type and keep TS
    // content because *we* know that if _type equals a thing then the type is
    // guarenteed but TS isn't so sure and needs assurance
    const elementsByType = (elements, type) => elements.filter((element) => element._type === type);
    const youTubeAtoms = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.YoutubeBlockElement');
    const quizAtoms = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.QuizAtomBlockElement');
    const callouts = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.CalloutBlockElement');
    const chartAtoms = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.ChartAtomBlockElement');
    const audioAtoms = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.AudioAtomBlockElement');
    const qandaAtoms = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.QABlockElement');
    const guideAtoms = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.GuideAtomBlockElement');
    const profileAtoms = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.ProfileAtomBlockElement');
    const timelineAtoms = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.TimelineBlockElement');
    const documents = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.DocumentBlockElement');
    const embeds = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.EmbedBlockElement');
    const instas = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.InstagramBlockElement');
    const maps = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.MapBlockElement');
    const spotifies = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.SpotifyBlockElement');
    const facebookVideos = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.VideoFacebookBlockElement');
    const vines = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.VineBlockElement');
    const richLinks = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.RichLinkBlockElement');
    const interactiveElements = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.InteractiveBlockElement');
    return (
    // Do you need to HydrateOnce or do you want a Portal?
    //
    // HydrateOnce: If your component is server rendered and you're hydrating it with
    // more data or making it interactive on the client and you do not need to access
    // global application state.
    //
    // Portal: If your component is not server rendered but a pure client-side component
    // and/or you want to access global application state, you want to use a Portal.
    //
    // Note: Both require a 'root' element that needs to be server rendered.
    React.createElement(React.StrictMode, null,
        React.createElement(Portal, { rootId: "reader-revenue-links-header" },
            React.createElement(ReaderRevenueLinks, { urls: CAPI.nav.readerRevenueLinks.header, edition: CAPI.editionId, countryCode: countryCode, dataLinkNamePrefix: "nav2 : ", inHeader: true, inRemoteModuleTest: inRemoteModuleTest, pageViewId: pageViewId, contributionsServiceUrl: CAPI.contributionsServiceUrl, ophanRecord: ophanRecord })),
        React.createElement(HydrateOnce, { rootId: "links-root", waitFor: [user] },
            React.createElement(Links, { supporterCTA: CAPI.nav.readerRevenueLinks.header.supporter, userId: user ? user.userId : undefined, idUrl: CAPI.config.idUrl, mmaUrl: CAPI.config.mmaUrl })),
        React.createElement(HydrateOnce, { rootId: "edition-root" },
            React.createElement(EditionDropdown, { edition: CAPI.editionId, dataLinkName: "nav2 : topbar : edition-picker: toggle" })),
        React.createElement(HydrateOnce, { rootId: "labs-header" },
            React.createElement(LabsHeader, null)),
        React.createElement(Portal, { rootId: "share-count-root" },
            React.createElement(ShareCount, { ajaxUrl: CAPI.config.ajaxUrl, pageId: CAPI.pageId })),
        youTubeAtoms.map((youTubeAtom) => (React.createElement(HydrateOnce, { rootId: youTubeAtom.elementId },
            React.createElement(YoutubeBlockComponent, { format: format, palette: palette, hideCaption: false, 
                // eslint-disable-next-line jsx-a11y/aria-role
                role: "inline", adTargeting: adTargeting, isMainMedia: false, id: youTubeAtom.id, assetId: youTubeAtom.assetId, channelId: youTubeAtom.channelId, expired: youTubeAtom.expired, overrideImage: youTubeAtom.overrideImage, posterImage: youTubeAtom.posterImage, duration: youTubeAtom.duration, mediaTitle: youTubeAtom.mediaTitle, altText: youTubeAtom.altText })))),
        interactiveElements.map((interactiveBlock) => (React.createElement(HydrateOnce, { rootId: interactiveBlock.elementId },
            React.createElement(InteractiveBlockComponent, { url: interactiveBlock.url, scriptUrl: interactiveBlock.scriptUrl, alt: interactiveBlock.alt, role: interactiveBlock.role, caption: interactiveBlock.caption, format: format, palette: palette })))),
        quizAtoms.map((quizAtom) => (React.createElement(HydrateOnce, { rootId: quizAtom.elementId },
            React.createElement(React.Fragment, null,
                quizAtom.quizType === 'personality' && (React.createElement(PersonalityQuizAtom, { id: quizAtom.id, questions: quizAtom.questions, resultBuckets: quizAtom.resultBuckets })),
                quizAtom.quizType === 'knowledge' && (React.createElement(KnowledgeQuizAtom, { id: quizAtom.id, questions: quizAtom.questions })))))),
        NAV.subNavSections && (React.createElement(HydrateOnce, { rootId: "sub-nav-root" },
            React.createElement(React.Fragment, null,
                React.createElement(SubNav, { subNavSections: NAV.subNavSections, currentNavLink: NAV.currentNavLink, palette: palette })))),
        CAPI.matchUrl && (React.createElement(Portal, { rootId: "match-nav" },
            React.createElement(GetMatchNav, { matchUrl: CAPI.matchUrl }))),
        richLinks.map((richLink, index) => (React.createElement(Portal, { rootId: richLink.elementId },
            React.createElement(RichLinkComponent, { element: richLink, pillar: pillar, ajaxEndpoint: CAPI.config.ajaxUrl, richLinkIndex: index })))),
        callouts.map((callout) => (React.createElement(HydrateOnce, { rootId: callout.elementId },
            React.createElement(CalloutBlockComponent, { callout: callout, palette: palette })))),
        chartAtoms.map((chartAtom) => (React.createElement(HydrateOnce, { rootId: chartAtom.elementId },
            React.createElement(ChartAtom, { id: chartAtom.id, html: chartAtom.html })))),
        audioAtoms.map((audioAtom) => (React.createElement(HydrateOnce, { rootId: audioAtom.elementId },
            React.createElement(AudioAtomWrapper, { id: audioAtom.id, trackUrl: audioAtom.trackUrl, kicker: audioAtom.kicker, title: audioAtom.title, pillar: pillar, contentIsNotSensitive: !CAPI.config.isSensitive, aCastisEnabled: CAPI.config.switches.acast, readerCanBeShownAds: !CAPI.isAdFreeUser })))),
        qandaAtoms.map((qandaAtom) => (React.createElement(HydrateOnce, { rootId: qandaAtom.elementId },
            React.createElement(QandaAtom, { id: qandaAtom.id, title: qandaAtom.title, html: qandaAtom.html, image: qandaAtom.img, credit: qandaAtom.credit, pillar: pillar, likeHandler: componentEventHandler('QANDA_ATOM', qandaAtom.id, 'LIKE'), dislikeHandler: componentEventHandler('QANDA_ATOM', qandaAtom.id, 'DISLIKE'), expandCallback: componentEventHandler('QANDA_ATOM', qandaAtom.id, 'EXPAND') })))),
        guideAtoms.map((guideAtom) => (React.createElement(HydrateOnce, { rootId: guideAtom.elementId },
            React.createElement(GuideAtom, { id: guideAtom.id, title: guideAtom.title, html: guideAtom.html, image: guideAtom.img, credit: guideAtom.credit, pillar: pillar, likeHandler: componentEventHandler('GUIDE_ATOM', guideAtom.id, 'LIKE'), dislikeHandler: componentEventHandler('GUIDE_ATOM', guideAtom.id, 'DISLIKE'), expandCallback: componentEventHandler('GUIDE_ATOM', guideAtom.id, 'EXPAND') })))),
        profileAtoms.map((profileAtom) => (React.createElement(HydrateOnce, { rootId: profileAtom.elementId },
            React.createElement(ProfileAtom, { id: profileAtom.id, title: profileAtom.title, html: profileAtom.html, image: profileAtom.img, credit: profileAtom.credit, pillar: pillar, likeHandler: componentEventHandler('PROFILE_ATOM', profileAtom.id, 'LIKE'), dislikeHandler: componentEventHandler('PROFILE_ATOM', profileAtom.id, 'DISLIKE'), expandCallback: componentEventHandler('PROFILE_ATOM', profileAtom.id, 'EXPAND') })))),
        timelineAtoms.map((timelineAtom) => (React.createElement(HydrateOnce, { rootId: timelineAtom.elementId },
            React.createElement(TimelineAtom, { id: timelineAtom.id, title: timelineAtom.title, events: timelineAtom.events, description: timelineAtom.description, pillar: pillar, likeHandler: componentEventHandler('TIMELINE_ATOM', timelineAtom.id, 'LIKE'), dislikeHandler: componentEventHandler('TIMELINE_ATOM', timelineAtom.id, 'DISLIKE'), expandCallback: componentEventHandler('TIMELINE_ATOM', timelineAtom.id, 'EXPAND') })))),
        documents.map((document) => (React.createElement(HydrateOnce, { rootId: document.elementId },
            React.createElement(ClickToView, { role: document.role, isTracking: document.isThirdPartyTracking, source: document.source, sourceDomain: document.sourceDomain },
                React.createElement(DocumentBlockComponent, { embedUrl: document.embedUrl, height: document.height, width: document.width, title: document.title, source: document.source }))))),
        embeds.map((embed, index) => (React.createElement(HydrateOnce, { rootId: embed.elementId }, embed.safe ? (React.createElement(ClickToView, { role: embed.role, isTracking: embed.isThirdPartyTracking, source: embed.source, sourceDomain: embed.sourceDomain },
            React.createElement(EmbedBlockComponent, { html: embed.html, alt: embed.alt }))) : (React.createElement(ClickToView, { role: embed.role, isTracking: embed.isThirdPartyTracking, source: embed.source, sourceDomain: embed.sourceDomain, onAccept: () => updateIframeHeight(`iframe[name="unsafe-embed-${index}"]`) },
            React.createElement(UnsafeEmbedBlockComponent, { key: embed.elementId, html: embed.html, alt: embed.alt || '', index: index })))))),
        instas.map((insta, index) => (React.createElement(HydrateOnce, { rootId: insta.elementId },
            React.createElement(ClickToView, { role: insta.role, isTracking: insta.isThirdPartyTracking, source: insta.source, sourceDomain: insta.sourceDomain, onAccept: () => updateIframeHeight(`iframe[name="instagram-embed-${index}"]`) },
                React.createElement(InstagramBlockComponent, { element: insta, index: index }))))),
        maps.map((map) => (React.createElement(HydrateOnce, { rootId: map.elementId },
            React.createElement(ClickToView, { role: map.role, isTracking: map.isThirdPartyTracking, source: map.source, sourceDomain: map.sourceDomain },
                React.createElement(MapEmbedBlockComponent, { format: format, palette: palette, embedUrl: map.embedUrl, height: map.height, width: map.width, caption: map.caption, credit: map.source, title: map.title }))))),
        spotifies.map((spotify) => (React.createElement(HydrateOnce, { rootId: spotify.elementId },
            React.createElement(ClickToView, { role: spotify.role, isTracking: spotify.isThirdPartyTracking, source: spotify.source, sourceDomain: spotify.sourceDomain },
                React.createElement(SpotifyBlockComponent, { embedUrl: spotify.embedUrl, height: spotify.height, width: spotify.width, title: spotify.title, format: format, palette: palette, caption: spotify.caption, credit: "Spotify" }))))),
        facebookVideos.map((facebookVideo) => (React.createElement(HydrateOnce, { rootId: facebookVideo.elementId },
            React.createElement(ClickToView, { role: facebookVideo.role, isTracking: facebookVideo.isThirdPartyTracking, source: facebookVideo.source, sourceDomain: facebookVideo.sourceDomain },
                React.createElement(VideoFacebookBlockComponent, { format: format, palette: palette, embedUrl: facebookVideo.embedUrl, height: facebookVideo.height, width: facebookVideo.width, caption: facebookVideo.caption, credit: facebookVideo.caption, title: facebookVideo.caption }))))),
        vines.map((vine) => (React.createElement(HydrateOnce, { rootId: vine.elementId },
            React.createElement(ClickToView
            // No role given by CAPI
            // eslint-disable-next-line jsx-a11y/aria-role
            , { 
                // No role given by CAPI
                // eslint-disable-next-line jsx-a11y/aria-role
                role: "inline", isTracking: vine.isThirdPartyTracking, source: vine.source, sourceDomain: vine.sourceDomain },
                React.createElement(VineBlockComponent, { element: vine }))))),
        React.createElement(Portal, { rootId: "most-viewed-right" },
            React.createElement(Lazy, { margin: 100 },
                React.createElement(Suspense, { fallback: React.createElement(React.Fragment, null) },
                    React.createElement(MostViewedRightWrapper, { palette: palette })))),
        CAPI.matchUrl && (React.createElement(Portal, { rootId: "match-stats" },
            React.createElement(Lazy, { margin: 300 },
                React.createElement(Suspense, { fallback: React.createElement(Placeholder, { height: 800 }) },
                    React.createElement(GetMatchStats, { matchUrl: CAPI.matchUrl }))))),
        React.createElement(Portal, { rootId: "slot-body-end" },
            React.createElement(SlotBodyEnd, { isSignedIn: isSignedIn, countryCode: countryCode, contentType: CAPI.contentType, sectionName: CAPI.sectionName, shouldHideReaderRevenue: CAPI.shouldHideReaderRevenue, isMinuteArticle: CAPI.pageType.isMinuteArticle, isPaidContent: CAPI.pageType.isPaidContent, tags: CAPI.tags, contributionsServiceUrl: CAPI.contributionsServiceUrl, brazeMessages: brazeMessages, idApiUrl: CAPI.config.idApiUrl })),
        React.createElement(Portal, { rootId: isSignedIn
                ? 'onwards-upper-whensignedin'
                : 'onwards-upper-whensignedout' },
            React.createElement(Lazy, { margin: 300 },
                React.createElement(Suspense, { fallback: React.createElement(React.Fragment, null) },
                    React.createElement(OnwardsUpper, { ajaxUrl: CAPI.config.ajaxUrl, hasRelated: CAPI.hasRelated, hasStoryPackage: CAPI.hasStoryPackage, isAdFreeUser: CAPI.isAdFreeUser, pageId: CAPI.pageId, isPaidContent: CAPI.config.isPaidContent || false, showRelatedContent: CAPI.config.showRelatedContent, keywordIds: CAPI.config.keywordIds, contentType: CAPI.contentType, tags: CAPI.tags, edition: CAPI.editionId, format: format })))),
        React.createElement(Portal, { rootId: isSignedIn
                ? 'onwards-lower-whensignedin'
                : 'onwards-lower-whensignedout' },
            React.createElement(Lazy, { margin: 300 },
                React.createElement(Suspense, { fallback: React.createElement(React.Fragment, null) },
                    React.createElement(OnwardsLower, { ajaxUrl: CAPI.config.ajaxUrl, hasStoryPackage: CAPI.hasStoryPackage, tags: CAPI.tags, format: format })))),
        React.createElement(Portal, { rootId: "sign-in-gate" },
            React.createElement(SignInGateSelector, { isSignedIn: isSignedIn, CAPI: CAPI })),
        React.createElement(HydrateOnce, { rootId: "comments", waitFor: [user] },
            React.createElement(Discussion, { discussionApiUrl: CAPI.config.discussionApiUrl, shortUrlId: CAPI.config.shortUrlId, isCommentable: CAPI.isCommentable, pillar: pillar, palette: palette, user: user || undefined, discussionD2Uid: CAPI.config.discussionD2Uid, discussionApiClientHeader: CAPI.config.discussionApiClientHeader, enableDiscussionSwitch: CAPI.config.enableDiscussionSwitch, isAdFreeUser: CAPI.isAdFreeUser, shouldHideAds: CAPI.shouldHideAds, beingHydrated: true, display: display })),
        React.createElement(Portal, { rootId: "most-viewed-footer" },
            React.createElement(MostViewedFooter, { palette: palette, sectionName: CAPI.sectionName, ajaxUrl: CAPI.config.ajaxUrl, display: display })),
        React.createElement(Portal, { rootId: "reader-revenue-links-footer" },
            React.createElement(Lazy, { margin: 300 },
                React.createElement(ReaderRevenueLinks, { urls: CAPI.nav.readerRevenueLinks.footer, edition: CAPI.editionId, countryCode: countryCode, dataLinkNamePrefix: "footer : ", inHeader: false, inRemoteModuleTest: false, pageViewId: pageViewId, contributionsServiceUrl: CAPI.contributionsServiceUrl, ophanRecord: ophanRecord }))),
        React.createElement(Portal, { rootId: "bottom-banner" },
            React.createElement(StickyBottomBanner, { isSignedIn: isSignedIn, asyncCountryCode: asyncCountryCode, CAPI: CAPI, brazeMessages: brazeMessages }))));
};
